import {
  Button,
  Card,
  CardBody,
  CardHeader,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from '@heroui/react'
import {
  type TenantAnalyticsFilters,
  type TenantAnalyticsResponseDto,
  useExportAllTenantCharts,
  useExportTenantChart,
  useGetLatestSnapshot,
  useGetTenantAnalytics,
} from '@qse/api'
import { UserRoleEnum } from '@qse/constants'
import { useUserStore } from '@qse/stores'
import { ErrorData, FormikControl, StatusChip, UsageProgressBar } from '@qse/ui'
import { formatNumber } from '@qse/utils'
import { Form, Formik, FormikHelpers } from 'formik'
import {
  ActivityIcon,
  ArrowLeftIcon,
  ChevronDownIcon,
  ClockIcon,
  CreditCardIcon,
  DownloadIcon,
  TrendingUpIcon,
  UsersIcon,
  WifiIcon,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import * as Yup from 'yup'

import { HistoryTable, RechartsLineChart, RechartsMultiLineChart } from '../../components/charts'
import { getSmartBandwidthUnitScale, getSmartUnitScale } from '../../utils/smart-unit-scaling'

interface DateRangeFormValues {
  startDate: string
  endDate: string
}

interface DateRangeOption {
  key: string
  label: string
  value: string
}

interface ChartCardProps {
  title: string
  icon: React.ComponentType<{ className?: string }>
  children: React.ReactNode
  onDownload?: () => void
  isDownloadDisabled?: boolean
  downloadTooltip?: string
  isDownloadLoading?: boolean
}

const ChartCard = ({
  title,
  icon: IconComponent,
  children,
  onDownload,
  isDownloadDisabled,
  downloadTooltip,
  isDownloadLoading,
}: ChartCardProps) => (
  <Card className="w-full shadow-none border border-gray-200 dark:border-[#2A2A2A]">
    <CardHeader className="flex justify-between items-center">
      <div className="flex items-center gap-2">
        <IconComponent className="w-5 h-5 text-primary" />
        <h3 className="text-lg font-medium">{title}</h3>
      </div>
      {onDownload && (
        <Button
          isIconOnly
          isDisabled={isDownloadDisabled}
          isLoading={isDownloadLoading}
          size="sm"
          title={downloadTooltip}
          variant="light"
          onPress={onDownload}
        >
          <DownloadIcon className="w-4 h-4" />
        </Button>
      )}
    </CardHeader>
    <CardBody>{children}</CardBody>
  </Card>
)

const ChartSkeleton = () => (
  <div className="space-y-4">
    {/* Chart title skeleton */}
    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 animate-pulse" />

    {/* Main chart area */}
    <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded" />

    {/* Legend items skeleton */}
    <div className="flex gap-4">
      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16 animate-pulse" />
      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20 animate-pulse" />
      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24 animate-pulse" />
    </div>
  </div>
)

const dateRangeOptions: DateRangeOption[] = [
  { key: 'last_7_days', label: 'Last 7 Days', value: 'last_7_days' },
  { key: 'last_30_days', label: 'Last 30 Days', value: 'last_30_days' },
  { key: 'last_90_days', label: 'Last 90 Days', value: 'last_90_days' },
  { key: 'custom', label: 'Custom Date Range', value: 'custom' },
]

const TenantAnalytics = () => {
  const { tenantId } = useParams<{ tenantId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useUserStore()
  const isSuperAdmin = user?.userRole === UserRoleEnum.SUPER_ADMIN

  // Helper functions to calculate current usage data
  const getCurrentStorageUsage = (analyticsData: TenantAnalyticsResponseDto | null) => {
    if (!analyticsData?.storageUsage?.length) {
      return { used: 0, limit: 0, percentage: 0, allocated: 0 }
    }

    // Get the most recent storage data point since storage usage is cumulative
    const latestStorageData = analyticsData.storageUsage[analyticsData.storageUsage.length - 1]
    const used = latestStorageData?.value || 0 // Now in bytes
    const limit = analyticsData.tenant?.storageUsageLimit || 0 // Also in bytes

    // Calculate percentage: if limit is 0 and used > 0, show 100% (over-quota)
    const percentage =
      limit === 0
        ? used > 0
          ? 100 // Show 100% when limit is 0 and used > 0
          : 0
        : limit > 0
          ? Number(((used / limit) * 100).toFixed(2))
          : 0

    return { used, limit, percentage, allocated: analyticsData.tenant?.storageUsageLimit || 0 }
  }

  const getCurrentApiUsage = (analyticsData: TenantAnalyticsResponseDto | null) => {
    if (!analyticsData?.apiUsage?.length || !analyticsData?.tenant?.apiUsageLimit) {
      return { used: 0, limit: 0, percentage: 0, allocated: 0 }
    }

    // Sum all API usage values since each day represents usage for that day (not cumulative)
    const used = analyticsData.apiUsage.reduce(
      (total, dataPoint) => total + (dataPoint?.value || 0),
      0,
    )
    const limit = analyticsData.tenant.apiUsageLimit
    const percentage = limit > 0 ? Number(((used / limit) * 100).toFixed(2)) : 0

    return { used, limit, percentage, allocated: analyticsData.tenant.apiUsageLimit }
  }

  // Helper function to transform bandwidth data and add total values
  const getBandwidthDataWithTotal = (analyticsData: TenantAnalyticsResponseDto | null) => {
    if (!analyticsData?.bandwidth?.length) {
      return []
    }

    return analyticsData.bandwidth.map(dataPoint => ({
      ...dataPoint,
      values: {
        ...dataPoint.values,
        total: (dataPoint.values.upload || 0) + (dataPoint.values.download || 0),
      },
    }))
  }

  // Helper function to transform user growth data and add total values
  const getUserGrowthDataWithTotal = (analyticsData: TenantAnalyticsResponseDto | null) => {
    if (!analyticsData?.userGrowth?.length) {
      return []
    }

    return analyticsData.userGrowth.map(dataPoint => ({
      ...dataPoint,
      values: {
        ...dataPoint.values,
        total:
          (dataPoint.values.active || 0) +
          (dataPoint.values.invited || 0) +
          (dataPoint.values.suspended || 0),
      },
    }))
  }

  // Helper function to get smart unit scaling for storage data
  const getStorageUnitScale = (analyticsData: TenantAnalyticsResponseDto | null) => {
    if (!analyticsData?.storageUsage?.length) {
      return {
        unit: 'GB' as const,
        formatValue: (value: number) => `${value.toFixed(3)}GB`,
        axisLabel: 'Storage (GB)',
      }
    }

    const values = analyticsData.storageUsage.map(dataPoint => dataPoint.value)

    return getSmartUnitScale(values, 'bytes')
  }

  // Helper function to get smart unit scaling for bandwidth data
  const getBandwidthUnitScale = (analyticsData: TenantAnalyticsResponseDto | null) => {
    if (!analyticsData?.bandwidth?.length) {
      return {
        unit: 'MB' as const,
        formatValue: (value: number) => `${value.toFixed(3)}MB`,
        axisLabel: 'Bandwidth (MB)',
      }
    }

    // Extract all bandwidth values (upload, download, total)
    const values: number[] = []

    analyticsData.bandwidth.forEach(dataPoint => {
      if (dataPoint.values.upload) values.push(dataPoint.values.upload)
      if (dataPoint.values.download) values.push(dataPoint.values.download)
      if (dataPoint.values.total) values.push(dataPoint.values.total)
    })

    return getSmartBandwidthUnitScale(values)
  }

  const getCurrentUserCount = (analyticsData: TenantAnalyticsResponseDto | null) => {
    if (!analyticsData?.userGrowth?.length) {
      return 0
    }

    // Get the most recent user growth data point and extract total users
    const latestUserData = analyticsData.userGrowth[analyticsData.userGrowth.length - 1]

    return latestUserData?.values?.active || 0
  }

  // Initialize from URL parameters
  const getInitialDateRange = useCallback(() => {
    const searchParams = new URLSearchParams(location.search)

    return {
      selectedRange: searchParams.get('selectedRange') || 'last_7_days',
      startDate: searchParams.get('startDate') || '',
      endDate: searchParams.get('endDate') || '',
    }
  }, [location.search])

  const initialValues = getInitialDateRange()

  const [selectedRange, setSelectedRange] = useState<string>(initialValues.selectedRange)
  const [isCustomRange, setIsCustomRange] = useState<boolean>(false)
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false)
  const [customDates, setCustomDates] = useState<{ startDate: string; endDate: string }>({
    startDate: initialValues.startDate,
    endDate: initialValues.endDate,
  })
  const [isSwitchingToCustom, setIsSwitchingToCustom] = useState<boolean>(false)
  const [exportingChartType, setExportingChartType] = useState<string | null>(null)

  // Initialize from URL parameters on mount and when location changes
  useEffect(() => {
    const {
      selectedRange: urlRange,
      startDate: urlStartDate,
      endDate: urlEndDate,
    } = getInitialDateRange()

    // Update state only if URL parameters are different from current state
    if (urlRange !== selectedRange) {
      setSelectedRange(urlRange)
    }

    if (urlStartDate !== customDates.startDate || urlEndDate !== customDates.endDate) {
      setCustomDates({
        startDate: urlStartDate,
        endDate: urlEndDate,
      })
    }

    // Set custom range state based on URL parameters
    if (urlRange === 'custom' && urlStartDate && urlEndDate) {
      setIsCustomRange(true)
    } else {
      setIsCustomRange(false)
    }
  }, [
    location.search,
    selectedRange,
    customDates.startDate,
    customDates.endDate,
    getInitialDateRange,
  ])

  // Function to update URL parameters
  const updateURLParams = useCallback(
    (newRange: string, newStartDate?: string, newEndDate?: string) => {
      const searchParams = new URLSearchParams(location.search)

      // Update or add the selected range parameter
      if (newRange && newRange !== 'last_7_days') {
        searchParams.set('selectedRange', newRange)
      } else {
        searchParams.delete('selectedRange')
      }

      // Update or add custom date parameters
      if (newRange === 'custom' && newStartDate && newEndDate) {
        searchParams.set('startDate', newStartDate)
        searchParams.set('endDate', newEndDate)
      } else {
        searchParams.delete('startDate')
        searchParams.delete('endDate')
      }

      // Navigate with updated parameters
      const newSearch = searchParams.toString()
      const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname

      navigate(newUrl, { replace: true })
    },
    [location.search, location.pathname, navigate],
  )

  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatDateToYYYYMMDD = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
  }

  const getDateRange = (key: string) => {
    // Always use today's date as the end date
    const today = new Date()

    today.setHours(0, 0, 0, 0)

    switch (key) {
      case 'last_7_days': {
        const last7Days = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000)

        return {
          startDate: formatDateToYYYYMMDD(last7Days),
          endDate: formatDateToYYYYMMDD(today),
        }
      }
      case 'last_30_days': {
        const last30Days = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)

        return {
          startDate: formatDateToYYYYMMDD(last30Days),
          endDate: formatDateToYYYYMMDD(today),
        }
      }
      case 'last_90_days': {
        const last90Days = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000)

        return {
          startDate: formatDateToYYYYMMDD(last90Days),
          endDate: formatDateToYYYYMMDD(today),
        }
      }
      default:
        return {}
    }
  }

  const filters = useMemo((): TenantAnalyticsFilters => {
    const baseFilters: TenantAnalyticsFilters = {}

    // Add date filters for all ranges
    if (selectedRange === 'custom' && customDates.startDate && customDates.endDate) {
      // Parse the date strings and get YYYY-MM-DD part only
      const startDateStr = customDates.startDate.split('T')[0] // Get YYYY-MM-DD part
      const endDateStr = customDates.endDate.split('T')[0] // Get YYYY-MM-DD part

      // Send dates in yyyy-mm-dd format as expected by the API
      baseFilters.startDate = startDateStr
      baseFilters.endDate = endDateStr
    } else {
      // For all preset ranges (including last_7_days), send startDate and endDate
      const dateRange = getDateRange(selectedRange)

      // Dates are already in yyyy-mm-dd format from formatDateToYYYYMMDD
      if (dateRange.startDate) {
        baseFilters.startDate = dateRange.startDate
      }
      if (dateRange.endDate) {
        baseFilters.endDate = dateRange.endDate
      }
    }

    return baseFilters
  }, [selectedRange, customDates])

  const {
    data: analyticsData,
    isLoading,
    error,
  } = useGetTenantAnalytics(tenantId || '', filters, {
    enabled: !!tenantId && isSuperAdmin,
  })

  const { data: latestSnapshotData, isLoading: isSnapshotLoading } = useGetLatestSnapshot(
    tenantId || '',
    {
      enabled: !!tenantId && isSuperAdmin,
    },
  )

  const { mutate: exportChart } = useExportTenantChart()
  const { mutate: exportAllCharts, isPending: isExportingAllCharts } = useExportAllTenantCharts()

  // Calculate date restrictions for custom date picker
  const maxDate = useMemo(() => {
    // Use today's date as maxDate
    const today = new Date()
    const maxYear = today.getFullYear()
    const maxMonth = today.getMonth()
    const maxDay = today.getDate()
    const maxDate = new Date(Date.UTC(maxYear, maxMonth, maxDay, 0, 0, 0))

    return maxDate
  }, [])

  const minDate = useMemo(() => {
    const min = new Date(maxDate)

    min.setFullYear(min.getFullYear() - 2) // 2 year in the past from today

    return min
  }, [maxDate])

  // Create validation schema with date restrictions
  const getValidationSchema = () => {
    return Yup.object({
      startDate: Yup.string()
        .required('Start date is required')
        .test(
          'min-date',
          `Start date must be after ${minDate.toLocaleDateString()}`,
          function (value) {
            if (!value) return true

            return new Date(value) >= minDate
          },
        )
        .test(
          'max-date',
          `Start date cannot be after ${maxDate.toLocaleDateString()}`,
          function (value) {
            if (!value) return true

            return new Date(value) <= maxDate
          },
        ),
      endDate: Yup.string()
        .required('End date is required')
        .test(
          'min-date',
          `End date must be after ${minDate.toLocaleDateString()}`,
          function (value) {
            if (!value) return true

            return new Date(value) >= minDate
          },
        )
        .test(
          'max-date',
          `End date cannot be after ${maxDate.toLocaleDateString()}`,
          function (value) {
            if (!value) return true

            return new Date(value) <= maxDate
          },
        )
        .test('date-range', 'End date must be after start date', function (value) {
          const { startDate } = this.parent

          if (!value || !startDate) return true

          return new Date(value) >= new Date(startDate)
        }),
    })
  }

  // Date range handlers
  const handleSelectOption = (key: string) => {
    if (key === 'custom') {
      setIsSwitchingToCustom(true)
      setIsCustomRange(true)
      setIsPopoverOpen(true)
    } else {
      setIsCustomRange(false)
      setSelectedRange(key)
      setIsPopoverOpen(false)
      // Update URL parameters for preset ranges
      updateURLParams(key)
    }
  }

  const handleFormSubmit = (
    values: DateRangeFormValues,
    { setSubmitting }: FormikHelpers<DateRangeFormValues>,
  ) => {
    setSelectedRange('custom')
    setCustomDates(values)
    setIsPopoverOpen(false)
    setIsSwitchingToCustom(false)
    setSubmitting(false)
    // Update URL parameters for custom date range
    updateURLParams('custom', values.startDate, values.endDate)
  }

  const handleCancel = () => {
    setIsCustomRange(false)
    setIsSwitchingToCustom(false)
  }

  const handleClearFilters = () => {
    setSelectedRange('last_7_days')
    setCustomDates({ startDate: '', endDate: '' })
    setIsCustomRange(false)
    setIsPopoverOpen(false)
    setIsSwitchingToCustom(false)
    // Clear URL parameters by navigating to the base URL
    navigate(location.pathname, { replace: true })
  }

  const getButtonText = () => {
    if (selectedRange === 'custom') {
      if (customDates.startDate && customDates.endDate) {
        return `${customDates.startDate} - ${customDates.endDate}`
      }

      return 'Custom Range'
    }
    const option = dateRangeOptions.find(opt => opt.key === selectedRange)

    return option ? option.label : 'Last 7 Days'
  }

  // Validation for custom date range
  const isValidDateRange = useMemo(() => {
    if (selectedRange !== 'custom' || !customDates.startDate || !customDates.endDate) return true

    const start = new Date(customDates.startDate)
    const end = new Date(customDates.endDate)

    // Check if dates are within valid range
    if (start < minDate || start > maxDate || end < minDate || end > maxDate) {
      return false
    }

    // Check if start date is before end date
    if (start >= end) {
      return false
    }

    return true
  }, [selectedRange, customDates, minDate, maxDate])

  const hasData =
    analyticsData &&
    (analyticsData.storageUsage.length > 0 ||
      analyticsData.apiUsage.length > 0 ||
      analyticsData.userGrowth.length > 0 ||
      analyticsData.bandwidth.length > 0)

  const isDownloadDisabled = !hasData || (selectedRange === 'custom' && !isValidDateRange)

  // Check if any filters are active (excluding default 'last_7_days')
  const hasActiveFilters =
    selectedRange !== 'last_7_days' &&
    (selectedRange !== 'custom' || customDates.startDate || customDates.endDate)

  // Redirect if not super admin or no tenant ID
  if (!isSuperAdmin || !tenantId) {
    navigate('/dashboard/management/storage-report')

    return null
  }

  const handleExportChart = (
    chartType:
      | 'storage-usage'
      | 'api-usage'
      | 'user-growth'
      | 'bandwidth'
      | 'status-history'
      | 'plan-history',
  ) => {
    if (!tenantId || !analyticsData) return
    setExportingChartType(chartType)
    exportChart(
      {
        tenantId,
        chartType,
        filters: {
          ...filters,
          tenantName: analyticsData.tenant.name, // Pass tenant name for filename
        } as TenantAnalyticsFilters & { tenantName: string },
      },
      {
        onSettled: () => {
          setExportingChartType(null)
        },
      },
    )
  }

  const handleExportAllCharts = () => {
    if (!tenantId || !analyticsData) return
    setExportingChartType('all-charts')
    exportAllCharts(
      {
        tenantId,
        filters: {
          ...filters,
          tenantName: analyticsData.tenant.name, // Pass tenant name for filename
        } as TenantAnalyticsFilters & { tenantName: string },
      },
      {
        onSettled: () => {
          setExportingChartType(null)
        },
      },
    )
  }

  if (error) {
    return (
      <section className="md:px-[30px] px-4 flex flex-col gap-5 pb-24 md:pb-20">
        <ErrorData
          title="Error Loading Analytics"
          description={
            error.message || 'An unexpected error occurred while loading tenant analytics.'
          }
        />
      </section>
    )
  }

  // Show error if no data is available and not loading
  if (!analyticsData && !isLoading) {
    return (
      <section className="md:px-[30px] px-4 flex flex-col gap-5 pb-24 md:pb-20">
        <ErrorData
          description="No analytics data available for this tenant or the selected period."
          title="No Data Available"
        />
      </section>
    )
  }

  return (
    <section className="md:px-[30px] px-4 flex flex-col gap-5 pb-24 md:pb-20 xl:pb-10">
      {/* Breadcrumb Navigation */}
      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-[#AFAFAF]">
        <button
          className="hover:text-primary transition-colors cursor-pointer"
          type="button"
          onClick={() => navigate(-1)}
        >
          Storage Reports
        </button>
        <span>{'>'}</span>
        <span
          className="text-gray-900 dark:text-white"
          style={{ wordBreak: 'break-all' }}
          title={(analyticsData?.tenant.name || 'Tenant') + ' Details'}
        >
          {analyticsData?.tenant.name || 'Tenant'} Details
        </span>
      </div>

      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div className="flex items-center gap-2 lg:gap-4">
          <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
            <ArrowLeftIcon className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="font-medium text-2xl">Tenant Analytics</h1>
            <p className="text-sm md:text-base">Detailed usage trends and activity patterns</p>
            {isSnapshotLoading ? (
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48 mt-1 animate-pulse" />
            ) : latestSnapshotData ? (
              <p className="text-xs text-gray-500 dark:text-[#AFAFAF] mt-1 flex flex-wrap items-center gap-1">
                <span>ℹ️ Report data reflects the last nightly snapshot generated on</span>
                <span className="font-medium">
                  {new Date(latestSnapshotData.date).toLocaleString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                    timeZone: 'UTC',
                  })}
                </span>
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          {/* Period Selection */}
          <div className="relative">
            <Popover
              isOpen={isPopoverOpen}
              placement="bottom-start"
              classNames={{
                content: `bg-white dark:bg-[#343537] border border-gray-300 dark:border-[#707070] text-gray-900 dark:text-gray-300 transition-all duration-500 ease-in-out ${isCustomRange ? 'max-w-[500px]' : '!w-[240px]'}`,
              }}
              onOpenChange={value => {
                // Prevent closing when switching to custom range mode
                if (!value && isSwitchingToCustom) {
                  setIsSwitchingToCustom(false)

                  return
                }
                setIsPopoverOpen(value)
              }}
            >
              <PopoverTrigger>
                <Button
                  className="min-w-[140px] w-full sm:w-auto bg-white dark:bg-[#343537] text-gray-900 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-[#707070]"
                  endContent={<ChevronDownIcon className="text-small flex-shrink-0" />}
                  radius="sm"
                  size="lg"
                  variant="bordered"
                >
                  {getButtonText()}
                </Button>
              </PopoverTrigger>

              <PopoverContent
                className={cn(
                  'bg-white dark:bg-[#343537] py-2 text-gray-900 dark:text-gray-300',
                  isCustomRange ? 'w-[500px]' : 'w-[240px]',
                )}
              >
                <div className={isCustomRange ? 'grid grid-cols-2 gap-4' : ''}>
                  <div>
                    {dateRangeOptions.map(option => (
                      <Button
                        key={option.key}
                        size="sm"
                        variant="light"
                        className={cn(
                          'w-full h-6 justify-start text-left text-gray-900 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600',
                          // Highlight selected option
                          selectedRange === option.key &&
                            'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 font-medium',
                        )}
                        onPress={() => handleSelectOption(option.key)}
                      >
                        {option.label}
                      </Button>
                    ))}
                  </div>

                  {isCustomRange && (
                    <div className="border-l transition-all duration-300 ease-in-out border-gray-300 dark:border-white pl-4 flex justify-center flex-col">
                      <Formik
                        enableReinitialize
                        validationSchema={getValidationSchema()}
                        initialValues={{
                          startDate: customDates.startDate,
                          endDate: customDates.endDate,
                        }}
                        onSubmit={handleFormSubmit}
                      >
                        {({ isSubmitting, isValid }) => (
                          <Form>
                            <div className="space-y-3">
                              <FormikControl
                                classNameContainer="w-full"
                                control="date"
                                label="Start Date"
                                maxValue={maxDate}
                                minValue={minDate}
                                name="startDate"
                                placeholder="Select start date"
                                isDateUnavailable={date => {
                                  // Normalize dates to beginning of day (00:00) for proper comparison
                                  const dateAtMidnight = new Date(
                                    date.getFullYear(),
                                    date.getMonth(),
                                    date.getDate(),
                                  )
                                  const maxDateAtMidnight = new Date(
                                    maxDate.getFullYear(),
                                    maxDate.getMonth(),
                                    maxDate.getDate(),
                                  )
                                  const minDateAtMidnight = new Date(
                                    minDate.getFullYear(),
                                    minDate.getMonth(),
                                    minDate.getDate(),
                                  )

                                  // Disable dates beyond max date (today)
                                  if (dateAtMidnight > maxDateAtMidnight) return true
                                  // Disable dates before min date (2 year ago from today)
                                  if (dateAtMidnight < minDateAtMidnight) return true

                                  return false
                                }}
                              />
                              <FormikControl
                                classNameContainer="w-full"
                                control="date"
                                label="End Date"
                                maxValue={maxDate}
                                minValue={minDate}
                                name="endDate"
                                placeholder="Select end date"
                                isDateUnavailable={date => {
                                  // Normalize dates to beginning of day (00:00) for proper comparison
                                  const dateAtMidnight = new Date(
                                    date.getFullYear(),
                                    date.getMonth(),
                                    date.getDate(),
                                  )
                                  const maxDateAtMidnight = new Date(
                                    maxDate.getFullYear(),
                                    maxDate.getMonth(),
                                    maxDate.getDate(),
                                  )
                                  const minDateAtMidnight = new Date(
                                    minDate.getFullYear(),
                                    minDate.getMonth(),
                                    minDate.getDate(),
                                  )

                                  // Disable dates beyond max date (today)
                                  if (dateAtMidnight > maxDateAtMidnight) return true
                                  // Disable dates before min date (2 year ago from today)
                                  if (dateAtMidnight < minDateAtMidnight) return true

                                  return false
                                }}
                              />
                              <div className="flex justify-end -mr-3">
                                <Button
                                  className="bg-transparent -mr-3"
                                  size="sm"
                                  onPress={handleCancel}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  className="bg-transparent"
                                  isDisabled={isSubmitting || !isValid}
                                  size="sm"
                                  type="submit"
                                >
                                  {isSubmitting ? 'Applying...' : 'Apply'}
                                </Button>
                              </div>
                            </div>
                          </Form>
                        )}
                      </Formik>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {/* Clear button for date filter */}
            {hasActiveFilters && (
              <button
                className="absolute -right-2 -top-2 w-5 h-5 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors z-10"
                title="Clear date filter"
                type="button"
                onClick={handleClearFilters}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M6 18L18 6M6 6l12 12"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Download All Charts */}

          <Tooltip
            isDisabled={isDownloadDisabled || !(selectedRange === 'custom' && !isValidDateRange)}
            content={
              !hasData
                ? 'No data available for the selected period'
                : selectedRange === 'custom' && !isValidDateRange
                  ? 'Please select a valid date range'
                  : undefined
            }
          >
            <div>
              <Button
                className="w-full sm:w-auto"
                color="primary"
                isDisabled={isDownloadDisabled}
                isLoading={isExportingAllCharts}
                radius="sm"
                size="lg"
                startContent={<DownloadIcon className="w-4 h-4" />}
                title={
                  !hasData
                    ? 'No data available for the selected period'
                    : selectedRange === 'custom' && !isValidDateRange
                      ? 'Please select a valid date range'
                      : undefined
                }
                onPress={handleExportAllCharts}
              >
                <span className="hidden sm:inline">Download All Charts</span>
                <span className="sm:hidden">Download All</span>
              </Button>
            </div>
          </Tooltip>
        </div>
      </div>

      {/* Contextual Header Bar - Tenant Info (matching Storage Report table row format) */}
      {isLoading ? (
        <div className="bg-white dark:bg-[#1A1A1A] rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-4">
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-9 gap-4">
            {Array.from({ length: 9 }).map((_, index) => (
              <div key={index} className="space-y-2">
                {/* Label skeleton */}
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16 animate-pulse" />

                {/* Value skeleton with different widths for variety */}
                <div className="space-y-1">
                  <div
                    className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"
                    style={{
                      width: `${Math.random() * 40 + 60}%`,
                      animationDelay: `${index * 0.1}s`,
                    }}
                  />
                  {/* Additional line for some items */}
                  {index === 0 && (
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24 animate-pulse" />
                  )}
                  {/* Progress bar skeleton for usage items */}
                  {(index === 6 || index === 7) && (
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                      <div
                        className="bg-gray-300 dark:bg-gray-600 h-2 rounded-full animate-pulse"
                        style={{
                          width: `${Math.random() * 60 + 20}%`,
                          animationDelay: `${index * 0.1 + 0.5}s`,
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : analyticsData ? (
        <div className="bg-white dark:bg-[#1A1A1A] rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-4">
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-9 gap-4 text-sm">
            <div>
              <span className="text-gray-500 dark:text-[#AFAFAF] block text-xs font-medium">
                Tenant Name
              </span>
              <div className="flex flex-col gap-1 font-light text-sm mt-1">
                <p className="text-base font-medium truncate" title={analyticsData.tenant.name}>
                  {analyticsData.tenant.name}
                </p>
                <p className="text-sm text-gray-500 dark:text-[#AFAFAF]">
                  ID: {analyticsData.tenant.id}
                </p>
              </div>
            </div>
            <div>
              <span className="text-gray-500 dark:text-[#AFAFAF] block text-xs font-medium">
                Type
              </span>
              <span className="text-gray-900 dark:text-white text-sm mt-1 block">
                {analyticsData.tenant.type}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-[#AFAFAF] block text-xs font-medium">
                Status
              </span>
              <div className="mt-1">
                <StatusChip status={analyticsData.tenant.status} />
              </div>
            </div>
            <div>
              <span className="text-gray-500 dark:text-[#AFAFAF] block text-xs font-medium">
                Plan Name
              </span>
              <span className="text-gray-900 dark:text-white mt-1 block">
                {analyticsData.tenant.planName || 'N/A'}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-[#AFAFAF] block text-xs font-medium">
                Next Billing Cycle
              </span>
              <span className="text-gray-900 dark:text-white text-sm mt-1 block">
                {(() => {
                  const billingDate =
                    analyticsData.tenant.billingPeriodEnd ||
                    analyticsData.tenant.nextBillingCycleDate

                  return billingDate && billingDate !== ''
                    ? new Date(billingDate).toLocaleDateString()
                    : 'N/A'
                })()}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-[#AFAFAF] block text-xs font-medium">
                Active Users
              </span>
              <span className="text-gray-900 dark:text-white font-medium mt-1 block">
                {getCurrentUserCount(analyticsData) || 0}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-[#AFAFAF] block text-xs font-medium">
                Storage Usage
              </span>
              <div className="mt-1">
                {(() => {
                  const storageUsage = getCurrentStorageUsage(analyticsData)

                  return (
                    <UsageProgressBar
                      allocated={storageUsage.allocated}
                      className="max-w-40"
                      limit={storageUsage.limit}
                      percentage={storageUsage.percentage}
                      unit="bytes"
                      used={storageUsage.used}
                    />
                  )
                })()}
              </div>
            </div>
            <div>
              <span className="text-gray-500 dark:text-[#AFAFAF] block text-xs font-medium">
                API Usage
              </span>
              <div className="mt-1">
                {(() => {
                  const apiUsage = getCurrentApiUsage(analyticsData)

                  return (
                    <UsageProgressBar
                      allocated={apiUsage.allocated}
                      className="max-w-40"
                      limit={apiUsage.limit || 0}
                      percentage={apiUsage.percentage}
                      unit=" calls/month"
                      used={apiUsage.used || 0}
                    />
                  )
                })()}
              </div>
            </div>
            <div>
              <span className="text-gray-500 dark:text-[#AFAFAF] block text-xs font-medium">
                Registration Date
              </span>
              <span className="text-gray-900 dark:text-white text-sm mt-1 block">
                {analyticsData.tenant.registrationDate
                  ? new Date(analyticsData.tenant.registrationDate).toLocaleDateString()
                  : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      ) : null}

      {/* Charts Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 gap-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <Card
              key={index}
              className="w-full shadow-none border border-gray-200 dark:border-[#2A2A2A]"
            >
              <CardHeader className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  {/* Icon skeleton */}
                  <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                  {/* Title skeleton */}
                  <div
                    className="h-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"
                    style={{
                      width: `${Math.random() * 40 + 120}px`,
                      animationDelay: `${index * 0.1}s`,
                    }}
                  />
                </div>
                {/* Download button skeleton */}
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </CardHeader>
              <CardBody>
                <ChartSkeleton />
              </CardBody>
            </Card>
          ))}
        </div>
      ) : analyticsData ? (
        <div className="grid grid-cols-1 gap-6">
          {/* Storage Usage Chart */}
          <ChartCard
            icon={TrendingUpIcon}
            isDownloadLoading={exportingChartType === 'storage-usage'}
            title="Storage Usage Over Time"
            downloadTooltip={
              analyticsData.storageUsage.length === 0
                ? 'No data available'
                : selectedRange === 'custom' && !isValidDateRange
                  ? 'Please select a valid date range'
                  : undefined
            }
            isDownloadDisabled={
              analyticsData.storageUsage.length === 0 ||
              (selectedRange === 'custom' && !isValidDateRange)
            }
            onDownload={() => handleExportChart('storage-usage')}
          >
            {analyticsData.storageUsage.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-[#AFAFAF]">
                No storage usage data available
              </div>
            ) : (
              (() => {
                const storageUnitScale = getStorageUnitScale(analyticsData)

                return (
                  <RechartsLineChart
                    color="#378A92"
                    formatValue={storageUnitScale.formatValue}
                    height={250}
                    showArea={true}
                    title="Storage Usage Over Time"
                    xAxisLabel="Date"
                    yAxisLabel={storageUnitScale.axisLabel}
                    data={analyticsData.storageUsage.map(dataPoint => ({
                      date: dataPoint.date,
                      value: dataPoint.value,
                      label: dataPoint.label,
                    }))}
                  />
                )
              })()
            )}
          </ChartCard>

          {/* Bandwidth Usage Chart */}
          <ChartCard
            icon={WifiIcon}
            isDownloadLoading={exportingChartType === 'bandwidth'}
            title="Bandwidth Usage"
            downloadTooltip={
              analyticsData.bandwidth.length === 0
                ? 'No data available'
                : selectedRange === 'custom' && !isValidDateRange
                  ? 'Please select a valid date range'
                  : undefined
            }
            isDownloadDisabled={
              analyticsData.bandwidth.length === 0 ||
              (selectedRange === 'custom' && !isValidDateRange)
            }
            onDownload={() => handleExportChart('bandwidth')}
          >
            {analyticsData.bandwidth.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-[#AFAFAF]">
                No bandwidth data available
              </div>
            ) : (
              (() => {
                const bandwidthUnitScale = getBandwidthUnitScale(analyticsData)

                return (
                  <RechartsMultiLineChart
                    data={getBandwidthDataWithTotal(analyticsData)}
                    formatValue={bandwidthUnitScale.formatValue}
                    height={250}
                    title="Bandwidth Usage"
                    xAxisLabel="Date"
                    yAxisLabel={bandwidthUnitScale.axisLabel}
                    lines={{
                      upload: { color: '#378A92', label: 'Upload' },
                      download: { color: '#F59E0B', label: 'Download' },
                      total: { color: '#8B5CF6', label: 'Total' },
                    }}
                  />
                )
              })()
            )}
          </ChartCard>

          {/* API Usage Chart */}
          <ChartCard
            icon={ActivityIcon}
            isDownloadLoading={exportingChartType === 'api-usage'}
            title="API Usage Over Time"
            downloadTooltip={
              analyticsData.apiUsage.length === 0
                ? 'No data available'
                : selectedRange === 'custom' && !isValidDateRange
                  ? 'Please select a valid date range'
                  : undefined
            }
            isDownloadDisabled={
              analyticsData.apiUsage.length === 0 ||
              (selectedRange === 'custom' && !isValidDateRange)
            }
            onDownload={() => handleExportChart('api-usage')}
          >
            {analyticsData.apiUsage.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-[#AFAFAF]">
                No API usage data available
              </div>
            ) : (
              <RechartsMultiLineChart
                formatValue={(value: number) => formatNumber(value)}
                height={250}
                title="API Usage Over Time"
                xAxisLabel="Date"
                yAxisLabel="Count"
                data={analyticsData.apiUsage.map(item => ({
                  date: item.date,
                  values: {
                    usage: item.value,
                    limit: item.allocated || 0,
                  },
                }))}
                lines={{
                  usage: {
                    label: 'API Usage',
                    color: '#F59E0B',
                  },
                  limit: {
                    label: 'API Limit',
                    color: '#378A92',
                  },
                }}
              />
            )}
          </ChartCard>

          {/* User Growth Chart */}
          <ChartCard
            icon={UsersIcon}
            isDownloadLoading={exportingChartType === 'user-growth'}
            title="User Growth"
            downloadTooltip={
              analyticsData.userGrowth.length === 0
                ? 'No data available'
                : selectedRange === 'custom' && !isValidDateRange
                  ? 'Please select a valid date range'
                  : undefined
            }
            isDownloadDisabled={
              analyticsData.userGrowth.length === 0 ||
              (selectedRange === 'custom' && !isValidDateRange)
            }
            onDownload={() => handleExportChart('user-growth')}
          >
            {analyticsData.userGrowth.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-[#AFAFAF]">
                No user growth data available
              </div>
            ) : (
              <RechartsMultiLineChart
                data={getUserGrowthDataWithTotal(analyticsData)}
                formatValue={(value: number) => formatNumber(value)}
                height={250}
                title="User Growth"
                xAxisLabel="Date"
                yAxisLabel="Count"
                lines={{
                  total: { color: '#378A92', label: 'Total Users' },
                  active: { color: '#10B981', label: 'Active Users' },
                }}
              />
            )}
          </ChartCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Status History Timeline */}
            <ChartCard
              icon={ClockIcon}
              isDownloadLoading={exportingChartType === 'status-history'}
              title="Status History"
              downloadTooltip={
                analyticsData.statusHistory.length === 0
                  ? 'No data available'
                  : selectedRange === 'custom' && !isValidDateRange
                    ? 'Please select a valid date range'
                    : undefined
              }
              isDownloadDisabled={
                analyticsData.statusHistory.length === 0 ||
                (selectedRange === 'custom' && !isValidDateRange)
              }
              onDownload={() => handleExportChart('status-history')}
            >
              <HistoryTable
                currentValue={analyticsData.tenant.status}
                data={analyticsData.statusHistory}
                type="status"
              />
            </ChartCard>

            {/* Plan History Timeline */}
            <ChartCard
              icon={CreditCardIcon}
              isDownloadLoading={exportingChartType === 'plan-history'}
              title="Plan History"
              downloadTooltip={
                analyticsData.planHistory.length === 0
                  ? 'No data available'
                  : selectedRange === 'custom' && !isValidDateRange
                    ? 'Please select a valid date range'
                    : undefined
              }
              isDownloadDisabled={
                analyticsData.planHistory.length === 0 ||
                (selectedRange === 'custom' && !isValidDateRange)
              }
              onDownload={() => handleExportChart('plan-history')}
            >
              <HistoryTable
                currentValue={analyticsData.tenant.planName}
                data={analyticsData.planHistory}
                type="plan"
              />
            </ChartCard>
          </div>
        </div>
      ) : null}
    </section>
  )
}

export default TenantAnalytics
